import { Button, Card, Space, Table, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useContext, useEffect, useState } from "react";
import {
  CustomerProps,
  TagProps,
  TaskProps,
} from "../../interfaces/interfaces";
import {
  EditOutlined,
  CopyOutlined,
  MailOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  IssuesCloseOutlined,
  PhoneOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataContext from "../../context/DataContext";
import Link from "antd/es/typography/Link";
import { TablePaginationConfig } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

export const TaskList = ({
  origin,
  onChangePage,
  onAddNewTask,
  onEditTask,
  onDuplicateTask,
  onDeleteTask,
  onSelectCustomer,
}: {
  origin: "dashboard" | "project" | "customer";
  onChangePage: (current: number, pageSize: number) => void;
  onAddNewTask: () => void;
  onEditTask: (record: TaskProps, index?: number) => void;
  onDuplicateTask: (record: TaskProps) => void;
  onDeleteTask?: (
    record: TaskProps,
    index?: number,
    pageSize?: number,
    currentPage?: number,
  ) => void;
  onSelectCustomer?: (customer: string) => void;
}) => {
  const { dataLoading, taskList } = useContext(DataContext);
  const [tableParams, setTableParams] = useState<TableParams>({});
  const [pageSize, setPageSize] = useState<number>(5);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [cardBodyVisible, setCardBodyVisible] = useState(
    origin === "customer" || origin === "project" || origin === "dashboard",
  );

  useEffect(() => {
    onChangePage(currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    setTableParams({
      pagination: {
        pageSize: pageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        showTotal: (total) => `Total ${total} Tasks`,
        position: ["bottomCenter"],
        total: parseInt(taskList.pagination?.total) || 0,
        onChange: (page, pageSize) => {
          setCurrentPage(page);
          setTableParams((prev) => {
            return {
              ...prev,
              pagination: {
                ...prev.pagination,
                pageSize: pageSize,
              },
            };
          });
          setPageSize(pageSize);
        },
      },
    });
  }, [taskList.pagination]);

  const taskColumns: ColumnsType<TaskProps> = [
    {
      title: "Actions",
      key: "edit",
      render: (params, _, index) => (
        <Space direction="horizontal">
          <Tooltip title="Edit" placement="left">
            <Button
              style={{ color: "gray", borderColor: "gray" }}
              onClick={() => onEditTask(params, index)}
              icon={<EditOutlined />}
              size="small"
            ></Button>
          </Tooltip>
          <Tooltip title="Duplicate" placement="top">
            <Button
              style={{ color: "gray", borderColor: "gray" }}
              onClick={() => onDuplicateTask(params)}
              icon={<CopyOutlined />}
              size="small"
            ></Button>
          </Tooltip>
          {onDeleteTask && (
            <Tooltip title="Delete" placement="right">
              <Button
                style={{ color: "gray", borderColor: "gray" }}
                onClick={() =>
                  onDeleteTask(params, index, pageSize, currentPage)
                }
                icon={<DeleteOutlined />}
                size="small"
              ></Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "typeOfTask",
      key: "typeOfTask",
      render: (typeOfTask: TaskProps["typeOfTask"]) =>
        typeOfTask === "Call" ? (
          <>
            <Tag color="lime" icon={<PhoneOutlined />}>
              Call
            </Tag>
          </>
        ) : typeOfTask === "Email" ? (
          <>
            <Tag color="cyan" icon={<MailOutlined />}>
              Email
            </Tag>
          </>
        ) : typeOfTask === "Follow-up" ? (
          <>
            <Tag color="magenta" icon={<CheckCircleOutlined />}>
              Follow-up
            </Tag>
          </>
        ) : typeOfTask === "Meeting" ? (
          <>
            <Tag color="geekblue" icon={<TeamOutlined />}>
              Meeting
            </Tag>
          </>
        ) : (
          <Tag color="orange" icon={<IssuesCloseOutlined />}>
            {typeOfTask}
          </Tag>
        ),
      sorter: (a, b) => a.typeOfTask.localeCompare(b.typeOfTask),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 200,
      render: (customer: CustomerProps) => (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            maxWidth: 180,
          }}
        >
          {customer && customer._id && (
            <div style={{ minWidth: "120px" }}>
              {onSelectCustomer ? (
                <Link>{customer?.DisplayName || ""}</Link>
              ) : (
                <div>{customer?.DisplayName || ""}</div>
              )}
            </div>
          )}
        </div>
      ),
      onCell: (record) => {
        return {
          onClick: () =>
            record.customer?._id && onSelectCustomer?.(record.customer?._id),
        };
      },
      hidden: origin === "customer",
    },
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      render: (project) => (
        <Tooltip title={project?.name || "-"}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              maxWidth: 180,
            }}
          >
            {project?.name || "-"}
          </div>
        </Tooltip>
      ),
      responsive: ["xl", "lg"],
      sorter: (a, b) => {
        const nameA = a.project?.name || "";
        const nameB = b.project?.name || "";
        return nameA.localeCompare(nameB);
      },
      hidden: origin === "customer",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (description) => (
        <Tooltip title={description}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              maxWidth: 180,
            }}
          >
            {description}
          </div>
        </Tooltip>
      ),
      responsive: ["xl", "lg"],
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (priority: string) => (
        <Tag
          color={
            priority === "High"
              ? "red"
              : priority === "Medium"
                ? "orange"
                : "green"
          }
        >
          {priority}
        </Tag>
      ),
      sorter: (a, b) => a.priority.localeCompare(b.priority),

      responsive: ["xl", "lg"],
    },
    {
      title: "Date",
      dataIndex: "startDate",
      className: "align-center",
      key: "startDate",
      render: (startDate: string) =>
        startDate ? dayjs(startDate).format("MM/DD/YYYY") : "-",
      sorter: (a, b) =>
        (a.startDate
          ? dayjs(a.startDate).format("YYYY-MM-DD")
          : ""
        ).localeCompare(
          b.startDate ? dayjs(b.startDate).format("YYYY-MM-DD") : "",
        ),
      responsive: ["xl", "lg"],
    },
    {
      title: "Next",
      dataIndex: "nextDate",
      className: "align-center",
      key: "nextDate",
      render: (nextDate: string) =>
        nextDate ? dayjs(nextDate).format("MM/DD/YYYY") : "-",
      sorter: (a, b) =>
        (a.nextDate
          ? dayjs(a.nextDate).format("YYYY-MM-DD")
          : ""
        ).localeCompare(
          b.nextDate ? dayjs(b.nextDate).format("YYYY-MM-DD") : "",
        ),
      responsive: ["xl", "lg"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag
          color={
            status === "Pending"
              ? "red"
              : status === "In Progress"
                ? "gold"
                : status === "Cancelled"
                  ? "blue"
                  : "green"
          }
        >
          {status}
        </Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: "20%",
      render: (tags: TagProps[]) => (
        <div>
          {tags?.map((tag: TagProps) => (
            <Tag
              key={tag._id}
              color={tag.color}
              bordered={false}
              style={{
                maxWidth: "120px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tag.name}
            </Tag>
          ))}
        </div>
      ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Recurring",
      dataIndex: "isRecurring",
      key: "isRecurring",
      render: (isRecurring: boolean, record: TaskProps) =>
        isRecurring ? <Tag color="purple">{record.frequency}</Tag> : "-",
      sorter: (a, b) =>
        (a.frequency ? a.frequency : "").localeCompare(
          b.frequency ? b.frequency : "",
        ),
      responsive: ["xl"],
    },
    {
      title: "Contact",
      dataIndex: "relatedContact",
      key: "relatedContact",

      render: (_, row) => (
        <div
          style={{
            maxWidth: "120px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.relatedContact?.name}
        </div>
      ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Assigned",
      dataIndex: "assignedTo",
      key: "assignedTo",
      render: (_, row) => (
        <>
          {Array.isArray(row.assignedTo) ? (
            row.assignedTo?.map((assigned) => (
              <div
                key={assigned._id}
                color="blue"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  maxWidth: 180,
                }}
              >
                {assigned?.name}
              </div>
            ))
          ) : (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                maxWidth: 180,
              }}
            >
              {row.assignedTo?.name}
            </div>
          )}
        </>
      ),
      responsive: ["xl", "lg", "md"],
      hidden: origin === "dashboard",
    },
  ];

  return (
    <Card
      hoverable
      title={
        <Space>
          <div>Tasks</div>
          <Tooltip title="New">
            <Button icon={<PlusOutlined />} onClick={onAddNewTask}></Button>
          </Tooltip>
        </Space>
      }
      style={{ marginBottom: 16 }}
      extra={
        <Button
          type="link"
          icon={cardBodyVisible ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setCardBodyVisible((v) => !v)}
        >
          {cardBodyVisible ? "Close Tasks" : "Open Tasks"}
        </Button>
      }
    >
      {cardBodyVisible && (
        <Table
          loading={dataLoading}
          columns={taskColumns}
          dataSource={taskList.data}
          rowKey="_id"
          pagination={tableParams.pagination}
          size="small"
          tableLayout="auto"
          style={{ width: "100%", overflowX: "scroll", minHeight: "200px" }}
          locale={{ emptyText: "No tasks " }}
        />
      )}
    </Card>
  );
};
